import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { authAPI } from '../../../api/auth'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import toast from 'react-hot-toast'
import { 
  Wallet, 
  Lock,
  Eye,
  EyeOff
} from 'lucide-react'

// Import background hero image
// Place your bg-hero image in src/assets/images/ as bg-hero.jpg, bg-hero.png, or bg-hero.webp
// Supported formats: .jpg, .jpeg, .png, .webp
// If the image doesn't exist, you'll need to add it to src/assets/images/bg-hero.jpg
import bgHeroImage from '../../../assets/images/bg-hero.jpg'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({ email: '', password: '' })
  const { login } = useAuth()
  const navigate = useNavigate()

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      return 'Email is required'
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address'
    }
    return ''
  }

  // Password validation
  const validatePassword = (password) => {
    if (!password) {
      return 'Password is required'
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters'
    }
    return ''
  }

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }))
    }
  }

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const value = e.target.value
    setPassword(value)
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: validatePassword(value) }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Validate inputs
    const emailError = validateEmail(email)
    const passwordError = validatePassword(password)
    
    setErrors({
      email: emailError,
      password: passwordError,
    })

    // Don't proceed if there are validation errors
    if (emailError || passwordError) {
      toast.error('Please fix the errors in the form', {
        duration: 3000,
      })
      return false
    }

    setLoading(true)

    try {
      const result = await login(email, password)
      if (result.success) {
        toast.success('Login successful!', {
          duration: 3000,
        })
        // Small delay to show success message before navigation
        setTimeout(() => {
          navigate('/dashboard')
        }, 500)
        return false
      } else {
        toast.error(result.error || 'Login failed. Please check your credentials and try again.', {
          duration: 5000,
        })
        // Don't clear form data on failed login
        setLoading(false)
        return false
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Invalid credentials or an error occurred. Please try again.'
      toast.error(errorMessage, {
        duration: 5000,
      })
      // Don't clear form data on error
      setLoading(false)
      return false
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Hero Section - Left Side */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom right, #004852, #003a42, #002c32)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 py-16 text-white w-full overflow-y-auto">
          {/* Welcome Text */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Welcome back!</h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Log in to manage your transactions, explore new features and continue managing your business seamlessly.
            </p>
          </div>

          {/* Hero Image Element */}
          {bgHeroImage && (
            <img 
              src={bgHeroImage} 
              alt="Hero" 
              className="w-full h-auto object-cover"
            />
          )}

          {/* Decorative Elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Login Form - Right Side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white px-4 py-8 lg:py-0">
        <div className="w-full max-w-lg lg:max-w-2xl">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-2 bg-primary-500 rounded-lg">
                <Wallet className="text-white" size={24} />
              </div>
              <h1 className="text-3xl font-bold text-primary-600">PRIMEXPAY</h1>
            </div>
            <p className="text-gray-600">Merchant Dashboard</p>
          </div>

          <div className="bg-white rounded-2xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <div className="lg:hidden mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
                <p className="text-gray-600 mt-2">Sign in to your account</p>
              </div>
              <div className="hidden lg:block">
                <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
                <p className="text-gray-600 mt-2">Sign in to your account to continue</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="space-y-2">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setErrors(prev => ({ ...prev, email: validateEmail(email) }))}
                  required
                  autoComplete="email"
                  placeholder="merchant@example.com"
                  className={errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={() => setErrors(prev => ({ ...prev, password: validatePassword(password) }))}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`pr-10 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                loading={loading}
                disabled={!email || !password || loading}
                onClick={(e) => {
                  // Additional safety to prevent form submission
                  if (loading) {
                    e.preventDefault()
                  }
                }}
              >
                <Lock className="mr-2" size={18} />
                Sign In
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                Secure login powered by PRIMEXPAY
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage

